import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  WhatsApp as WechatIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import api from '../api';
import ConfirmDialog from '../components/ConfirmDialog';

const CONTACT_TYPES = [
  { value: 'supplier', label: '供应商', color: 'primary' },
  { value: 'worker', label: '工人', color: 'success' },
  { value: 'customer', label: '客户', color: 'warning' },
  { value: 'equipment', label: '设备租赁', color: 'info' },
  { value: 'other', label: '其他', color: 'default' },
];

export default function ContactList() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });
  const [editingContact, setEditingContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [newContact, setNewContact] = useState({
    name: '',
    type: 'supplier',
    company: '',
    phone: '',
    wechat: '',
    email: '',
    address: '',
    specialty: '',
    notes: '',
    rating: 0,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchContacts();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, typeFilter]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (typeFilter) params.type = typeFilter;
      const response = await api.get('/api/contacts', { params });
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const submitLock = useRef(false); // 写锁：与 QuotationDetail.writeLock 同款防连点

  const handleSave = async () => {
    if (submitLock.current) return; // 防连点：请求在途时忽略重复提交
    submitLock.current = true;
    try {
      try {
        if (editingContact) {
          await api.put(`/api/contacts/${editingContact.id}`, newContact);
        } else {
          await api.post('/api/contacts', {
            ...newContact,
            id: Date.now().toString(),
          });
        }
        setOpenDialog(false);
        setEditingContact(null);
        resetForm();
        fetchContacts();
      } catch (error) {
        console.error('Error saving contact:', error);
      }
    } finally {
      submitLock.current = false;
    }
  };

  const handleDelete = async (id) => {
    setConfirmDialog({ open: true, id });
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/api/contacts/${confirmDialog.id}`);
      setConfirmDialog({ open: false, id: null });
      fetchContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setNewContact({
      name: contact.name,
      type: contact.type,
      company: contact.company || '',
      phone: contact.phone || '',
      wechat: contact.wechat || '',
      email: contact.email || '',
      address: contact.address || '',
      specialty: contact.specialty || '',
      notes: contact.notes || '',
      rating: contact.rating || 0,
    });
    setOpenDialog(true);
  };

  const resetForm = () => {
    setNewContact({
      name: '',
      type: 'supplier',
      company: '',
      phone: '',
      wechat: '',
      email: '',
      address: '',
      specialty: '',
      notes: '',
      rating: 0,
    });
  };

  const getTypeInfo = (type) => {
    return CONTACT_TYPES.find(t => t.value === type) || CONTACT_TYPES[4];
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <StarIcon
        key={i}
        sx={{ color: i < rating ? 'gold' : 'grey.300', fontSize: 16 }}
      />
    ));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">通讯录</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingContact(null);
            resetForm();
            setOpenDialog(true);
          }}
        >
          添加联系人
        </Button>
      </Box>

      {/* 搜索和筛选 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            placeholder="搜索姓名、公司、电话、微信..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            label="类型筛选"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <MenuItem value="">全部类型</MenuItem>
            {CONTACT_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {/* 联系人列表 */}
      {contacts.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" align="center" color="text.secondary">
              暂无联系人
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary">
              点击"添加联系人"添加供应商、工人、客户等
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {contacts.map((contact) => {
            const typeInfo = getTypeInfo(contact.type);
            return (
              <Grid item xs={12} sm={6} md={4} key={contact.id}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="h6">{contact.name}</Typography>
                        {contact.company && (
                          <Typography variant="body2" color="text.secondary">
                            {contact.company}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        label={typeInfo.label}
                        color={typeInfo.color}
                        size="small"
                      />
                    </Box>

                    {contact.specialty && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        专长：{contact.specialty}
                      </Typography>
                    )}

                    <Box sx={{ mt: 1 }}>
                      {contact.phone && (
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">{contact.phone}</Typography>
                        </Box>
                      )}
                      {contact.wechat && (
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <WechatIcon fontSize="small" color="action" />
                          <Typography variant="body2">{contact.wechat}</Typography>
                        </Box>
                      )}
                    </Box>

                    {contact.rating > 0 && (
                      <Box sx={{ mt: 1 }}>
                        {renderStars(contact.rating)}
                      </Box>
                    )}

                    {contact.notes && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {contact.notes}
                      </Typography>
                    )}

                    <Box display="flex" justifyContent="flex-end" sx={{ mt: 1 }}>
                      <IconButton size="small" onClick={() => handleEdit(contact)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(contact.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* 添加/编辑对话框 */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingContact ? '编辑联系人' : '添加联系人'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="姓名 *"
                value={newContact.name}
                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="类型"
                value={newContact.type}
                onChange={(e) => setNewContact({ ...newContact, type: e.target.value })}
              >
                {CONTACT_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="公司/单位"
                value={newContact.company}
                onChange={(e) => setNewContact({ ...newContact, company: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="电话"
                value={newContact.phone}
                onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="微信"
                value={newContact.wechat}
                onChange={(e) => setNewContact({ ...newContact, wechat: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="专长/业务"
                value={newContact.specialty}
                onChange={(e) => setNewContact({ ...newContact, specialty: e.target.value })}
                placeholder="如：高空车租赁、PVC管批发、电缆供应..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="地址"
                value={newContact.address}
                onChange={(e) => setNewContact({ ...newContact, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="备注"
                multiline
                rows={2}
                value={newContact.notes}
                onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom>评分</Typography>
              <Box>
                {[1, 2, 3, 4, 5].map((star) => (
                  <IconButton
                    key={star}
                    onClick={() => setNewContact({ ...newContact, rating: star })}
                  >
                    <StarIcon sx={{ color: star <= newContact.rating ? 'gold' : 'grey.300' }} />
                  </IconButton>
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>取消</Button>
          <Button onClick={handleSave} variant="contained" disabled={!newContact.name}>
            {editingContact ? '保存' : '添加'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, id: null })}
        onConfirm={handleConfirmDelete}
        title="确认删除"
        message="确定要删除这个联系人吗？"
      />
    </Box>
  );
}
